import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search, Subscriptions, CalendarMonth, Person } from '@mui/icons-material';
import axios from 'axios';

function SuperAdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function fetchSubscriptions() {
      try {
        // We can reuse the super-admin payments endpoint as subscriptions are based on payment orders
        const response = await axios.get('/api/payment/super-admin/payments');
        // Filter only captured payments as they represent successful subscriptions
        setSubscriptions(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription history:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchSubscriptions();
  }, []);

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const searchStr = searchTerm.toLowerCase();
    return (
      (sub.first_name?.toLowerCase() || '').includes(searchStr) ||
      (sub.last_name?.toLowerCase() || '').includes(searchStr) ||
      (sub.email?.toLowerCase() || '').includes(searchStr) ||
      (sub.plan_code?.toLowerCase() || '').includes(searchStr) ||
      (sub.razorpay_order_id?.toLowerCase() || '').includes(searchStr)
    );
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography
            variant="h4"
            sx={{
              color: "#4F46E5",
              fontWeight: "bold",
              mb: 2
            }}
        >
          Subscription History
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 4, borderRadius: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by user name, email, plan or order ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plan Details</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subscription Period</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubscriptions.map((sub) => (
              <TableRow key={sub.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="action" fontSize="small" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {sub.first_name} {sub.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={sub.plan_code} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    ₹{sub.amount / 100}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={sub.status} 
                    color={sub.status === 'captured' ? 'success' : (sub.status === 'failed' ? 'error' : 'warning')} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonth fontSize="inherit" color="action" />
                    <Typography variant="caption">
                      {new Date(sub.created_at).toLocaleDateString()} - {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {sub.payment_id || sub.razorpay_order_id || 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filteredSubscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No subscription records found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}

export default SuperAdminSubscriptions;
